/**
 * taskEngineConfig.tsx — konfiguracja silnika formatek dla kartoteki Zadania (faza 30).
 * Wzorzec: `employeeEngineConfig.tsx`.
 *
 * Wszystkie pola core mają własny renderer (etykiety z katalogu pól: `ctx.field.name`).
 * Pola, których formularz nie edytuje wprost:
 *  - `task_type_id` — typ zmienia akcja „Zmień typ" (przycisk obok pola, `extra.onChangeType`),
 *    bo zmiana typu nadaje nowy numer po stronie serwera;
 *  - `system_number` — numer nadaje serwer (przed zapisem: „Nadawany po zapisaniu");
 *  - `created_by_user_id` — autor, tylko do odczytu;
 *  - `company_id` — NIE ma pola w formularzu: firma wynika z typu zadania.
 * Status i priorytet to stałe enumy w kodzie (nie wartości z katalogu pól).
 */

import { useTranslation } from 'react-i18next';
import { TASK_PRIORITIES, TASK_STATUSES } from '@saas/shared';

import { Button } from '@/components/Button';
import { CoreInput } from '@/components/CoreInput';
import { Dropdown, type DropdownOption } from '@/components/Dropdown';
import { Field } from '@/components/kartoteki/CoreFieldLayout';
import type {
  CoreFieldConfig,
  FieldRenderContext,
  FieldRenderer,
  KartotekaEngineConfig,
} from '@/components/kartoteki/engine/engine-config';
import { RecordCategoryPicker } from '@/components/record-categories/RecordCategoryPicker';
import { Textarea } from '@/components/Textarea';
import { ContractorSearchPicker } from '@/components/ui/ContractorSearchPicker';
import { UserPicker } from '@/components/ui/UserPicker';
import { priorityLabel, statusLabel } from '@/pages/tasks/task-labels';
import { isTaskPriority, isTaskStatus } from '@/pages/tasks/task-model';

import type { TaskDateField, TaskFormData } from './task-form';

/** Stan runtime niesiony przez `extra` (silnik go nie interpretuje). */
export interface TaskExtra {
  /** Firma zadania (z typu) — zawęża pickery osoby i kontrahenta. */
  companyId: string | null;
  /** Etykieta typu „Nazwa (KOD)" do pola tylko-do-odczytu. */
  taskTypeLabel: string;
  /** Akcja „Zmień typ" — `null` gdy niedostępna (nowe zadanie, brak prawa, zadanie usunięte). */
  onChangeType: (() => void) | null;
}

type Ctx = FieldRenderContext<TaskFormData, TaskExtra>;

/** Slug pola core → klucz stanu formularza (`company_id` celowo bez wpisu). */
export const TASK_CORE_FIELD_SLUGS: Record<string, keyof TaskFormData> = {
  title: 'title',
  system_number: 'systemNumber',
  status: 'status',
  priority: 'priority',
  due_at: 'dueAt',
  owner_user_id: 'ownerUserId',
  task_type_id: 'taskTypeId',
  contractor_id: 'contractorId',
  description: 'description',
  category_id: 'categoryId',
  created_by_user_id: 'createdByName',
  planned_start_at: 'plannedStartAt',
  planned_end_at: 'plannedEndAt',
  actual_start_at: 'actualStartAt',
  actual_end_at: 'actualEndAt',
};

const TASK_FIELD_TYPES: Record<string, string> = {
  title: 'varchar',
  system_number: 'varchar',
  status: 'select',
  priority: 'select',
  due_at: 'timestamp',
  owner_user_id: 'reference',
  task_type_id: 'reference',
  contractor_id: 'reference',
  description: 'text',
  category_id: 'select',
  created_by_user_id: 'reference',
  planned_start_at: 'timestamp',
  planned_end_at: 'timestamp',
  actual_start_at: 'timestamp',
  actual_end_at: 'timestamp',
};

const DROPDOWN_INVALID_CLASS = 'border-red-500 ring-1 ring-red-500';

const titleRenderer: FieldRenderer<TaskFormData, TaskExtra> = (ctx) => (
  <Field label={ctx.field.name} required error={ctx.getError?.('title')} colSpan={2}>
    <CoreInput
      testId="task-field-title"
      value={ctx.form.title}
      onChange={(v) => {
        ctx.clearError?.('title');
        ctx.setField({ title: v });
      }}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('title')}
      maxLength={255}
    />
  </Field>
);

function SystemNumberField({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  return (
    <Field label={ctx.field.name}>
      <CoreInput
        testId="task-field-number"
        value={ctx.form.systemNumber ?? t('tenant.tasks.detail.numberPending')}
        onChange={() => {}}
        readOnly
        disabled
        variant="mutedDisabled"
      />
    </Field>
  );
}

function TaskTypeField({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const onChangeType = ctx.extra?.onChangeType ?? null;
  return (
    <Field label={ctx.field.name}>
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <CoreInput
            testId="task-field-type"
            value={ctx.extra?.taskTypeLabel ?? ''}
            onChange={() => {}}
            readOnly
            disabled
            variant="mutedDisabled"
          />
        </div>
        {onChangeType !== null && (
          <Button
            variant="secondary"
            size="sm"
            onClick={onChangeType}
            data-testid="task-change-type-btn"
          >
            {t('tenant.tasks.detail.changeType')}
          </Button>
        )}
      </div>
    </Field>
  );
}

function StatusField({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const options: DropdownOption[] = TASK_STATUSES.map((s) => ({
    value: s,
    label: statusLabel(t, s),
  }));
  return (
    <Field label={ctx.field.name} required error={ctx.getError?.('status')}>
      <Dropdown
        options={options}
        value={ctx.form.status}
        disabled={!ctx.editable}
        onChange={(v) => {
          if (isTaskStatus(v)) ctx.setField({ status: v });
        }}
        className={ctx.hasError?.('status') ? DROPDOWN_INVALID_CLASS : undefined}
        data-testid="task-field-status"
      />
    </Field>
  );
}

function PriorityField({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const options: DropdownOption[] = TASK_PRIORITIES.map((p) => ({
    value: p,
    label: priorityLabel(t, p),
  }));
  return (
    <Field label={ctx.field.name} required error={ctx.getError?.('priority')}>
      <Dropdown
        options={options}
        value={ctx.form.priority}
        disabled={!ctx.editable}
        onChange={(v) => {
          if (isTaskPriority(v)) ctx.setField({ priority: v });
        }}
        className={ctx.hasError?.('priority') ? DROPDOWN_INVALID_CLASS : undefined}
        data-testid="task-field-priority"
      />
    </Field>
  );
}

/** Renderer pola daty z godziną (`datetime-local`, czas lokalny przeglądarki). */
function dateRenderer(key: TaskDateField): FieldRenderer<TaskFormData, TaskExtra> {
  return (ctx) => (
    <Field label={ctx.field.name} error={ctx.getError?.(key)}>
      <CoreInput
        type="datetime-local"
        testId={`task-field-${key}`}
        value={ctx.form[key]}
        onChange={(v) => {
          ctx.clearError?.(key);
          ctx.setField({ [key]: v } as Pick<TaskFormData, TaskDateField>);
        }}
        disabled={!ctx.editable}
        invalid={ctx.hasError?.(key)}
      />
    </Field>
  );
}

/** Osoba odpowiedzialna — opcjonalna, bez wstępnego wypełnienia; lista = osoby z dostępem do firmy zadania. */
const ownerRenderer: FieldRenderer<TaskFormData, TaskExtra> = (ctx) => (
  <Field label={ctx.field.name} error={ctx.getError?.('ownerUserId')}>
    <UserPicker
      value={ctx.form.ownerUserId}
      onChange={(id, row) => {
        ctx.clearError?.('ownerUserId');
        ctx.setField({
          ownerUserId: id,
          ownerName: id === null ? null : (row?.displayName ?? row?.email ?? ctx.form.ownerName),
        });
      }}
      companyId={ctx.extra?.companyId ?? null}
      selectedLabel={ctx.form.ownerName}
      disabled={!ctx.editable}
      data-testid="task-field-owner"
    />
  </Field>
);

/** Kontrahent — opcjonalny, bez osoby kontaktowej; lista = kontrahenci widoczni w firmie zadania. */
const contractorRenderer: FieldRenderer<TaskFormData, TaskExtra> = (ctx) => (
  <Field label={ctx.field.name} error={ctx.getError?.('contractorId')}>
    <ContractorSearchPicker
      value={ctx.form.contractorId ?? ''}
      onChange={(id, row) => {
        ctx.clearError?.('contractorId');
        ctx.setField({
          contractorId: id === '' ? null : id,
          contractorName: id === '' ? null : (row?.name ?? ctx.form.contractorName),
        });
      }}
      selectedLabel={ctx.form.contractorName}
      companyId={ctx.extra?.companyId ?? null}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('contractorId')}
      data-testid="task-field-contractor"
    />
  </Field>
);

const descriptionRenderer: FieldRenderer<TaskFormData, TaskExtra> = (ctx) => (
  <Field label={ctx.field.name} colSpan={2} error={ctx.getError?.('description')}>
    <Textarea
      rows={4}
      value={ctx.form.description}
      onChange={(e) => ctx.setField({ description: e.target.value })}
      disabled={!ctx.editable}
      data-testid="task-field-description"
    />
  </Field>
);

const categoryRenderer: FieldRenderer<TaskFormData, TaskExtra> = (ctx) => (
  <RecordCategoryPicker
    kartotekaKey="tasks"
    value={ctx.form.categoryId}
    onChange={(next) => {
      ctx.clearError?.('categoryId');
      ctx.setField({ categoryId: next });
    }}
    editable={ctx.editable}
    testId="task-field-category"
  />
);

const createdByRenderer: FieldRenderer<TaskFormData, TaskExtra> = (ctx) => (
  <Field label={ctx.field.name}>
    <CoreInput
      testId="task-field-created-by"
      value={ctx.form.createdByName ?? ''}
      onChange={() => {}}
      readOnly
      disabled
      variant="mutedDisabled"
    />
  </Field>
);

const TASK_RENDERERS: Record<string, FieldRenderer<TaskFormData, TaskExtra>> = {
  title: titleRenderer,
  system_number: (ctx) => <SystemNumberField ctx={ctx} />,
  status: (ctx) => <StatusField ctx={ctx} />,
  priority: (ctx) => <PriorityField ctx={ctx} />,
  due_at: dateRenderer('dueAt'),
  owner_user_id: ownerRenderer,
  task_type_id: (ctx) => <TaskTypeField ctx={ctx} />,
  contractor_id: contractorRenderer,
  description: descriptionRenderer,
  category_id: categoryRenderer,
  created_by_user_id: createdByRenderer,
  planned_start_at: dateRenderer('plannedStartAt'),
  planned_end_at: dateRenderer('plannedEndAt'),
  actual_start_at: dateRenderer('actualStartAt'),
  actual_end_at: dateRenderer('actualEndAt'),
};

const fields: Record<string, CoreFieldConfig<TaskFormData, TaskExtra>> = {};
for (const slug of Object.keys(TASK_CORE_FIELD_SLUGS)) {
  const renderer = TASK_RENDERERS[slug];
  if (renderer === undefined) throw new Error(`Brak renderera pola zadania „${slug}"`);
  fields[slug] = {
    stateKey: TASK_CORE_FIELD_SLUGS[slug],
    fieldType: TASK_FIELD_TYPES[slug] ?? 'varchar',
    renderer,
    ...(slug === 'description' || slug === 'title' ? { colSpan: 2 as const } : {}),
  };
}

export const taskEngineConfig: KartotekaEngineConfig<TaskFormData, TaskExtra> = {
  moduleSlug: 'tasks',
  testIdPrefix: 'task-ext',
  coreFieldSlugs: Object.keys(TASK_CORE_FIELD_SLUGS),
  fields,
  coerceEmptyToNull: false,
};
